/**
 * What the native watch needs to know, written down where it can read it.
 *
 * The anchor watch failed silently because the check lived in a WebView Chromium
 * freezes in the background, and it turned out every other screen-off alarm had
 * the same disease: AIS collision, off-track and waypoint arrival all ran in the
 * same freezable JavaScript, behind arming screens that implied otherwise. The
 * checks have moved to the watch process (RouteWatch.java, AisWatch.java). These
 * builders produce the snapshots that process reads.
 *
 * Files rather than bridge state, for the same reason as the anchor: the whole
 * point is surviving the web app's death, and state that lives in the bridge
 * dies with it. Each snapshot is complete on its own, holding the thresholds as
 * well as the data, so the native side never has to correlate two files that
 * were written at different moments.
 *
 * AIS is the honest compromise in here. The feed is a WebSocket in the WebView,
 * so when the page freezes the targets stop updating and native cannot get new
 * ones. What it can do is dead-reckon the last known targets forward on their
 * own course and speed, which over the six-or-so minutes a target stays
 * trustworthy is exactly what a CPA is anyway: a projection. Past that window
 * the native side says it is blind rather than pretending, which is the entire
 * lesson of this week.
 */
import type { Route, Waypoint } from '@/routing';
import { effectiveLegXtd, effectiveWaypointRadius, clampLegIndex, APPROACH_LEG_INDEX } from '@/routing';
import type { AisTarget } from '@/store/ais';
import { isLost } from '@/ais/targetAge';

/** The one leg the off-track alarm measures against. */
export interface RouteWatchLeg {
  fromLon: number;
  fromLat: number;
  toLon: number;
  toLat: number;
  xtdNm: number;
  name: string;
}

export interface RouteWatchSnapshot {
  updatedMs: number;
  arrivalEnabled: boolean;
  offTrackEnabled: boolean;
  /** Null when not following a route, or on the approach leg (no planned track). */
  activeLeg: RouteWatchLeg | null;
  arrivals: { lon: number; lat: number; radiusNm: number; label: string }[];
}

export interface RouteWatchInputs {
  routes: Route[];
  marks: Waypoint[];
  followRouteId: string | null;
  activeLegIndex: number | null;
  activeRouteId: string | null;
}

/**
 * The route picture for the native side, or null when there is nothing to watch.
 *
 * Null rather than an empty snapshot, because "nothing armed" is what lets the
 * watch service shut down when the anchor is also up, and an empty-but-present
 * file would keep it running for no one.
 */
export function buildRouteWatchSnapshot(
  i: RouteWatchInputs,
  nowMs: number,
): RouteWatchSnapshot | null {
  const arrivals: RouteWatchSnapshot['arrivals'] = [];
  for (const m of i.marks) {
    if (typeof m.arrivalRadiusNm === 'number') {
      arrivals.push({
        lon: m.position[0],
        lat: m.position[1],
        radiusNm: m.arrivalRadiusNm,
        label: m.name || 'mark',
      });
    }
  }
  // Only the route being followed or selected, the same rule the on-screen
  // alarm applies: a waypoint on some other stored route in the same water
  // must not sound an arrival nobody is navigating to.
  for (const r of i.routes) {
    if (r.id !== i.followRouteId && r.id !== i.activeRouteId) continue;
    for (const w of r.waypoints) {
      const rad = effectiveWaypointRadius(r, w);
      if (rad != null) {
        arrivals.push({ lon: w.position[0], lat: w.position[1], radiusNm: rad, label: w.name || 'WP' });
      }
    }
  }

  let activeLeg: RouteWatchLeg | null = null;
  if (i.followRouteId && i.activeLegIndex != null && i.activeLegIndex !== APPROACH_LEG_INDEX) {
    const route = i.routes.find((r) => r.id === i.followRouteId);
    if (route && route.waypoints.length >= 2) {
      const idx = clampLegIndex(route, i.activeLegIndex);
      if (idx !== APPROACH_LEG_INDEX) {
        const from = route.waypoints[idx];
        const to = route.waypoints[idx + 1];
        if (from && to) {
          const xtd = effectiveLegXtd(route, from.id);
          if (xtd != null) {
            activeLeg = {
              fromLon: from.position[0],
              fromLat: from.position[1],
              toLon: to.position[0],
              toLat: to.position[1],
              xtdNm: xtd,
              name: to.name || 'WP',
            };
          }
        }
      }
    }
  }

  if (!arrivals.length && !activeLeg) return null;
  return {
    updatedMs: nowMs,
    arrivalEnabled: arrivals.length > 0,
    offTrackEnabled: activeLeg != null,
    activeLeg,
    arrivals,
  };
}

export interface AisSnapshotTarget {
  mmsi: number;
  label: string;
  lon: number;
  lat: number;
  cogDeg: number;
  sogKn: number;
  /** When this target's position was last heard, so native can dead-reckon and age it. */
  tMs: number;
}

export interface AisSnapshot {
  updatedMs: number;
  cpaNm: number;
  tcpaMin: number;
  targets: AisSnapshotTarget[];
}

/**
 * The AIS picture for the native side, or null when the CPA alarm is off or the
 * feed is not running. Lost targets are dropped here, the same cut the on-screen
 * CPA makes: a ghost's position is a guess, and a screen-off collision alarm
 * against a vessel that is not there teaches the crew to sleep through the one
 * that is.
 */
export function buildAisSnapshot(
  i: {
    enabled: boolean;
    cpaEnabled: boolean;
    cpaNm: number;
    tcpaMin: number;
    targets: Record<number, AisTarget>;
  },
  nowMs: number,
): AisSnapshot | null {
  if (!i.enabled || !i.cpaEnabled) return null;
  const targets: AisSnapshotTarget[] = [];
  for (const t of Object.values(i.targets)) {
    if (isLost(t, nowMs)) continue;
    if (!t.position || !Number.isFinite(t.cogDeg) || !Number.isFinite(t.sogKn)) continue;
    targets.push({
      mmsi: t.mmsi,
      label: t.name || String(t.mmsi),
      lon: t.position[0],
      lat: t.position[1],
      cogDeg: t.cogDeg,
      sogKn: t.sogKn,
      tMs: t.lastMs ?? nowMs,
    });
  }
  return { updatedMs: nowMs, cpaNm: i.cpaNm, tcpaMin: i.tcpaMin, targets };
}
